// AI Concierge ("Aria") — streaming chat, restyled to Luan's #app-ai ai-chat-full panel. Replies
// stream token-by-token. Concierge messages carry no PHI guarantee — Aria refuses
// medical questions server-side (guardrails) and the transcript is ephemeral (not persisted).
//
// The conversation is cached in a module-level, in-memory store (below) so it survives in-app
// navigation away from and back to this page — without writing the transcript to any storage, so it
// stays ephemeral (cleared on a full reload / tab close), keeping the no-persistence posture.
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Btn, Markdown } from "@championcare/ui";
import { conciergeStream, fetchConciergeHistory, sanitizeConciergeText, type ConciergeMessage } from "../lib/conciergeStream";
import { formatTime } from "../lib/format";
import { useAppAuth } from "../auth";

// Day separator label for the scrollback view (history spans up to 12 months).
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

const SendIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const GREETING =
  "Hi — I'm Aria, your Champion Care concierge. I can help you book a visit or answer questions about your membership. What can I do for you?";
const SUGGESTIONS = ["When is my next visit?", "What does my plan include?", "How do I book a visit?", "How do prescriptions work?"];

// Surface the booking/care-team CTA when Aria escalates (refusal) or talks about scheduling.
const ESCALATION_HINT = /\b(book|schedule|visit|appointment|care team|doctor|message)\b/i;

// A displayed message carries a client-side timestamp (ISO) for the in-chat time labels. The `ts` is
// display-only and stripped before the wire call (the API takes role+content only).
interface ChatMessage extends ConciergeMessage {
  ts: string;
}

// Module-level, in-memory conversation cache — survives unmount/remount on in-app navigation, but is
// NOT written to localStorage/sessionStorage, so the transcript is gone on a full reload / tab close.
const store: { messages: ChatMessage[]; startedAt: string | null; escalate: boolean } = {
  messages: [],
  startedAt: null,
  escalate: false,
};

export function Concierge() {
  const navigate = useNavigate();
  const { getToken } = useAppAuth();
  const [messages, setMessages] = useState<ChatMessage[]>(store.messages);
  const [startedAt, setStartedAt] = useState<string | null>(store.startedAt);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [live, setLive] = useState(""); // the reply currently streaming in
  const [escalate, setEscalate] = useState(store.escalate);
  const listRef = useRef<HTMLDivElement>(null);

  // Mirror state into the module store so navigating away and back restores the conversation.
  useEffect(() => {
    store.messages = messages;
  }, [messages]);
  useEffect(() => {
    store.startedAt = startedAt;
  }, [startedAt]);
  useEffect(() => {
    store.escalate = escalate;
  }, [escalate]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, live]);

  // Load the member's persisted transcript (past 12 months) once on mount, so the conversation
  // survives full reloads and is scrollable back through history. Server timestamps are authoritative.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchConciergeHistory(getToken)
      .then((hist) => {
        if (!hist.length) return;
        const msgs: ChatMessage[] = hist.map((m) => ({ role: m.role, content: m.content, ts: m.ts }));
        setMessages(msgs);
        setStartedAt(msgs[0]?.ts ?? null);
      })
      .catch(() => {});
  }, [getToken]);

  async function send(text: string) {
    // De-zalgo / sanitize before the message enters the transcript — so it can't blow past the API's
    // char cap, poison the cached history, or reach the model as garbage.
    const content = sanitizeConciergeText(text);
    if (!content || streaming) return;
    const now = new Date().toISOString();
    if (!startedAt) setStartedAt(now);
    const history: ChatMessage[] = [...messages, { role: "user", content, ts: now }];
    setMessages(history);
    setDraft("");
    setStreaming(true);
    setLive("");
    setEscalate(false);

    let acc = "";
    try {
      // Send only the recent tail for model context (the API caps a request at 50 messages); the full
      // transcript stays on screen. Wire payload is role+content only — display-only `ts` is dropped.
      const wire: ConciergeMessage[] = history.slice(-24).map(({ role, content }) => ({ role, content }));
      for await (const ev of conciergeStream(wire, getToken)) {
        if (ev.type === "chunk") {
          acc += ev.text;
          setLive(acc);
        } else {
          // refusal / error — discard the partial, show this text
          acc = ev.text;
          setLive(acc);
        }
      }
    } catch {
      acc = "Sorry — something went wrong. Please try again.";
    }

    setMessages([...history, { role: "assistant", content: acc, ts: new Date().toISOString() }]);
    setLive("");
    setStreaming(false);
    setEscalate(ESCALATION_HINT.test(acc));
  }

  const showGreeting = messages.length === 0 && !streaming;

  return (
    <>
      <div className="app-page-head">
        <h1>Aria <em>— your AI concierge.</em></h1>
        <p>Always-on guidance for scheduling and membership questions. Ask anything.</p>
      </div>

      <div className="ai-page-grid">
        <div className="ai-chat-full">
          <div className="ai-chat-head">
            <div className="ai-chat-avatar">A</div>
            <div className="ai-chat-info">
              <div className="ai-chat-name">Aria</div>
              <div className="ai-chat-meta">Online · here to help</div>
            </div>
          </div>

          <div className="ai-chat-body" ref={listRef}>
            {showGreeting && <div className="ai-msg bot"><Markdown text={GREETING} /></div>}
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const showDay = !prev || new Date(prev.ts).toDateString() !== new Date(m.ts).toDateString();
              return (
                <Fragment key={i}>
                  {showDay && <div className="ai-chat-started">{dayLabel(m.ts)}</div>}
                  <div className={`ai-msg-row ${m.role === "user" ? "user" : "bot"}`}>
                    <div className={`ai-msg ${m.role === "user" ? "user" : "bot"}`}>
                      {m.role === "user" ? m.content : <Markdown text={m.content} />}
                    </div>
                    <div className="ai-msg__time">{formatTime(m.ts)}</div>
                  </div>
                </Fragment>
              );
            })}
            {streaming && <div className="ai-msg bot">{live ? <Markdown text={live} /> : "…"}</div>}
            {escalate && !streaming && (
              <div className="ai-suggestions">
                <Btn variant="primary" className="btn--sm" onClick={() => navigate("/visits/book")}>Book a visit</Btn>
                <Btn variant="ghost" className="btn--sm" onClick={() => navigate("/messages")}>Message care team</Btn>
              </div>
            )}
          </div>

          <div className="ai-chat-foot">
            {showGreeting && (
              <div className="ai-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="ai-suggestion" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            )}
            <div className="ai-composer-row">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send(draft)}
                placeholder="Ask Aria anything…"
                aria-label="Message Aria"
                disabled={streaming}
              />
              <button disabled={!draft.trim() || streaming} onClick={() => send(draft)} aria-label="Send">
                {SendIcon}
              </button>
            </div>
            <p className="ai-emergency-note" role="note">
              <strong>Please Note:</strong> This service is not intended for emergency medical care. If
              you are experiencing a medical emergency, please call <a href="tel:911">911</a> or go to
              the nearest emergency department right away.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
